import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { FormattedMessage, InjectedIntl } from 'react-intl';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useAnalytics, withAnalytics } from 'src/components/settings/analytics';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMatch } from 'src/router/hooks';

import { Checkbox } from '@atlaskit/checkbox';
import FeedbackCollector from '@atlaskit/feedback-collector';
import Flag, { FlagGroup } from '@atlaskit/flag';
import { Label, RequiredAsterisk } from '@atlaskit/form';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import { token } from '@atlaskit/tokens';

import messages from './feedback-collector.i18n';
import { SummaryLabelContainer } from './styles';
import urls from './urls';

interface FeedbackCollectorInterface {
  isFeedbackCollectorModalOpen: boolean;
  setIsFeedbackCollectorModalOpen: (
    isFeedbackCollectorModalOpen: boolean
  ) => void;
  currentUserDisplayName?: string;
  currentUserAccountId?: string;
  feedbackMetadata?: any;
  intl: InjectedIntl;
}

export const FeedbackCollectorContext =
  createContext<FeedbackCollectorInterface>({
    isFeedbackCollectorModalOpen: false,
    setIsFeedbackCollectorModalOpen: () => {},
    intl: {} as InjectedIntl,
  });

const AIFeedbackCollectorModal = () => {
  const {
    isFeedbackCollectorModalOpen,
    setIsFeedbackCollectorModalOpen,
    currentUserDisplayName,
    currentUserAccountId,
    feedbackMetadata,
    intl,
  } = useContext(FeedbackCollectorContext);
  const [showSuccessFlag, setShowSuccessFlag] = useState(false);
  const [hasPromptConsent, setHasPromptConsent] = useState(false);
  const { publishTrackEvent } = useAnalytics();
  const { path } = useMatch();

  let source: string;
  switch (path) {
    case '/:repositoryOwner/:repositorySlug/pull-requests/new':
      source = 'createPullRequestAIFeedbackCollector';
      break;
    case '/:repositoryOwner/:repositorySlug/pull-requests/:pullRequestId':
      source = 'pullRequestAIFeedbackCollector';
      break;
    case '/:repositoryOwner/:repositorySlug/commits/:ref+':
      source = 'commitAIFeedbackCollector';
  }
  // The default source is 'AIFeedbackCollector' and is provided in `withAnalytics`.

  const sendAnalyticsEvent = () =>
    publishTrackEvent({
      source,
      action: 'submitted',
      actionSubject: 'aiFeedbackCollector',
      actionSubjectId: 'aiFeedbackCollectorSubmitted',
      attributes: { feedbackMetadata },
    });

  const GAIE_ENTRYPOINT_ID = '73cf1eae-42a3-418a-a7ac-976ba0b5735d';

  const handleHasPromptConsent = useCallback(() => {
    setHasPromptConsent(current => !current);
  }, []);

  const additionalFields = useMemo(() => {
    if (feedbackMetadata) {
      const aiExperience =
        feedbackMetadata.getAIExperience?.(hasPromptConsent) || {};
      let feedbackMetadataStringified = `sentiment: ${feedbackMetadata.sentiment}\n`;
      Object.entries({
        ...feedbackMetadata.editorAttributes,
        ...aiExperience,
      }).forEach(item => {
        feedbackMetadataStringified += `${item[0]}: ${item[1]}\n`;
      });
      return [{ id: 'customfield_10047', value: feedbackMetadataStringified }];
    }
    return [];
  }, [feedbackMetadata, hasPromptConsent]);

  const {
    giveFeedbackTitle,
    giveFeedbackPlaceholder,
    giveFeedbackSuccessTitle,
    giveFeedbackSuccessDescription,
    giveFeedbackPromptConsent,
  } = useMemo(
    () => ({
      giveFeedbackTitle: intl.formatMessage(messages.giveFeedbackTitle),
      giveFeedbackPlaceholder: intl.formatMessage(
        messages.giveFeedbackPlaceholder
      ),
      giveFeedbackSuccessTitle: intl.formatMessage(
        messages.giveFeedbackSuccessTitle
      ),
      giveFeedbackSuccessDescription: intl.formatMessage(
        messages.giveFeedbackSuccessDescription
      ),
      giveFeedbackPromptConsent: intl.formatMessage(
        messages.giveFeedbackPromptConsent
      ),
    }),
    [intl]
  );

  return (
    <>
      {isFeedbackCollectorModalOpen && (
        // This wrapper prevents feedback collector submit from surfacing to the create PR page
        <div onSubmit={e => e.stopPropagation()}>
          <FeedbackCollector
            locale={intl.locale}
            entrypointId={GAIE_ENTRYPOINT_ID}
            atlassianAccountId={currentUserAccountId}
            name={currentUserDisplayName}
            onClose={() => setIsFeedbackCollectorModalOpen(false)}
            onSubmit={() => {
              sendAnalyticsEvent();
              setShowSuccessFlag(true);
            }}
            additionalFields={additionalFields}
            showTypeField={false}
            feedbackTitle={giveFeedbackTitle}
            canBeContactedLabel={
              <FormattedMessage
                {...messages.canContactLabel}
                values={{
                  privacyPolicyLink: (
                    <a href={urls.external.privacyPolicy}>
                      <FormattedMessage {...messages.privacyPolicyLinkText} />
                    </a>
                  ),
                }}
                tagName="p"
              />
            }
            enrolInResearchLabel={
              <FormattedMessage
                {...messages.enrolLabel}
                values={{
                  atlassianResearchGroupLink: (
                    <a href={urls.external.atlassianResearchGroup}>
                      <FormattedMessage
                        {...messages.atlassianResearchGroupLinkText}
                      />
                    </a>
                  ),
                }}
                tagName="p"
              />
            }
            summaryPlaceholder={giveFeedbackPlaceholder}
            customContent={
              <>
                <Checkbox
                  label={giveFeedbackPromptConsent}
                  onChange={handleHasPromptConsent}
                  isChecked={hasPromptConsent}
                />
                <SummaryLabelContainer>
                  <Label htmlFor="">
                    <FormattedMessage {...messages.giveFeedbackSummaryTitle} />
                    <RequiredAsterisk />
                  </Label>
                </SummaryLabelContainer>
              </>
            }
          />
        </div>
      )}
      <FlagGroup onDismissed={() => setShowSuccessFlag(false)}>
        {showSuccessFlag && (
          <Flag
            id="editor-feedback-success"
            testId="editor-feedback-success-flag"
            title={giveFeedbackSuccessTitle}
            description={giveFeedbackSuccessDescription}
            icon={
              <SuccessIcon
                label="editor-feedback-success-icon"
                primaryColor={token('color.icon.success')}
              />
            }
          />
        )}
      </FlagGroup>
    </>
  );
};

export default withAnalytics(AIFeedbackCollectorModal, {
  source: 'AIFeedbackCollector',
});
